import { ExtensionAgentContextKey, ExtensionAgentTrigger, ExtensionKind } from './agent.definition';

export interface ExtensionAgentCapabilityDescriptor {
    contextKey: ExtensionAgentContextKey;
    version: string;
    name: string;
    description: string;
    triggers: { value: ExtensionAgentTrigger; label: string }[];
}

/**
 * What WorkBuddy knows about one source entity kind: its version, triggers and the fields a run's context may carry.
 * Frozen into every run's execution contract by `captureExtensionExecutionContract`; it grants no permissions, because a cell acts through the MCP server and public API with its run token's scopes.
 */
export interface ExtensionAgentCapability extends ExtensionAgentCapabilityDescriptor {
    sourceFields: string[];
    sourceSchema: { type: 'object'; required: string[]; properties: Record<string, { type: 'string' | 'object' }> };
}

/** Entity semantics are supplied by WorkBuddy; what a cell may read or change is governed by its run token's OAuth scopes on the MCP server and public API, not by this catalogue. */
const capabilities: ReadonlyArray<ExtensionAgentCapability> = [{
    contextKey: ExtensionAgentContextKey.Job,
    version: 'job:v1',
    name: 'Job',
    description: 'Job details, customer, site and status.',
    sourceFields: ['_id', 'jobNumber', 'name', 'status', 'customer', 'site'],
    sourceSchema: { type: 'object', required: ['_id'], properties: {
        _id: { type: 'string' }, jobNumber: { type: 'string' }, name: { type: 'string' }, status: { type: 'string' },
        customer: { type: 'object' }, site: { type: 'object' },
    } },
    triggers: [
        { value: ExtensionAgentTrigger.Updated, label: 'Job updated' },
        { value: ExtensionAgentTrigger.StatusChanged, label: 'Job status changed' },
    ],
}];

/**
 * The public API and MCP name of each internal source field a run's trigger may report, per capability version.
 * Kept beside the catalogue rather than inside a capability, because capabilities are frozen into contracts and served to the runtime, and these keys are internal names an agent must never see.
 * Values follow `transformPublicJob` and must be `PublicJobFieldValues` names, which a jms-api spec asserts; a field missing here is dropped, so audit and identity fields (`updatedAt`, `jobId`) never reach an agent.
 */
const PUBLIC_FIELD_NAMES: Record<string, Readonly<Record<string, string>>> = {
    'job:v1': {
        integrationId: 'integrationId',
        jobNumber: 'jobNumber',
        name: 'name',
        description: 'description',
        status: 'status',
        customer: 'customer',
        site: 'site',
        supervisor: 'customerSupervisor',
        type: 'type',
        mode: 'structure',
        priority: 'priority',
        owner: 'owner',
        fieldSupervisor: 'fieldSupervisor',
        zones: 'zones',
        labels: 'tags',
        additionalNote: 'notes',
        customerWO: 'customerWO',
        customerPO: 'customerPO',
        pricebook: 'priceBook',
        billingCompany: 'billingCompany',
        policy: 'policy',
        thresholds: 'thresholds',
        customFields: 'customFields',
        finance: 'finance',
        dueDate: 'dueDate',
        startDate: 'startDate',
        workOrders: 'stages',
    },
};

/** Translates internal changed-field names into a capability version's public names, first-seen order, no duplicates; unmapped fields and unknown versions yield nothing. Called by `ExtensionRunAccessService` when it builds a run's trigger. */
export function toPublicChangedFields(capabilityVersion: string, fields: readonly string[]): string[] {

    const names = PUBLIC_FIELD_NAMES[capabilityVersion] ?? {};
    const mapped = fields
        .filter(field => Object.hasOwn(names, field))
        .map(field => names[field]);
    return [...new Set(mapped)];
}

/** Returns a private copy of the capability for a context key, or undefined when WorkBuddy has none; called when a run's contract is captured and when a definition is validated on save. */
export function getExtensionAgentCapability(contextKey: string): ExtensionAgentCapability | undefined {

    const capability = capabilities.find(item => item.contextKey === contextKey);
    return capability ? structuredClone(capability) : undefined;
}

/** Authoring needs the available entities and events, not runtime credentials or implementation details. */
export function getExtensionAgentCapabilityDescriptors(): ExtensionAgentCapabilityDescriptor[] {

    return capabilities.map(({ contextKey, version, name, description, triggers }) => ({ contextKey, version, name, description, triggers: structuredClone(triggers) }));
}

/**
 * The trigger choices, with labels, that a definition of `kind` may pick for a context: the capability's triggers, and for a workflow also "created" first.
 * An agent acts only on records assigned to it and a new record has no assignment yet, so created would never reach one; a workflow is organisation-wide.
 * The one source for the save policy's allowed values, the workflow editor's options and the settings list's labels; an unknown context yields none.
 */
export function getExtensionTriggerOptions(kind: ExtensionKind, contextKey: string): ExtensionAgentCapabilityDescriptor['triggers'] {

    const capability = capabilities.find(item => item.contextKey === contextKey);
    if (!capability) {
        return [];
    }
    const triggers = structuredClone(capability.triggers);
    if (kind !== ExtensionKind.Workflow) {
        return triggers;
    }
    return [{ value: ExtensionAgentTrigger.Created, label: `${capability.name} created` }, ...triggers];
}
